import * as React from 'react';

import { translate } from '~l10n/client';
import Backbone from 'backbone';
import cx from 'classnames';

import { PaywallScreens } from '~analytics/types';
import { Badge } from '~shared/components/Badge';
import { IconButton } from '~shared/components/buttons';
import { FocusIndicator } from '~shared/components/FocusIndicator';
import { IconCheckmark, IconExternal } from '~shared/components/icons/standard';
import {
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuItemRenderPropArgs,
  MenuList,
} from '~shared/components/Menu';
import { PaywallDialogGeneric } from '~teacher/components/PaywallDialog';
import {
  SelectableTab,
  useMapRouteToSelectedTab,
} from '~teacher/context/CurrentRouteContext/hooks';
import { useAccount } from '~teacher/hook/account';
import { proCheck } from '~teacher/util/proCheck';

import { useShouldShowDiscoverTab } from '../useShouldShowDiscoverTab';

const Constants = require('Constants');
interface StyledItemProps extends MenuItemRenderPropArgs {
  children: React.ReactNode;
  isTitle?: boolean;
  isTabSelected?: boolean;
}
function StyledMenuItem({
  children,
  isTitle,
  isActive,
  isTabSelected = false,
  ...props
}: StyledItemProps) {
  const buttonClass = cx(
    'flex w-full items-center pl-2 pr-8 py-3 whitespace-wrap cursor-pointer',
    'font-sans text-base text-blue-900 leading-8 text-left border-0 rounded-1',
    isActive ? 'bg-cyan-50' : 'bg-white'
  );

  const titleClass = cx(
    'font-sans text-xs font-700 leading-8 text-grey-900 tracking-wider uppercase pl-1 pr-8 py-3 m-0 bg-white truncate'
  );

  return (
    <div
      className={cx(
        'flex flex-row items-center rounded-1 px-4',
        isActive ? 'bg-cyan-50' : 'bg-white'
      )}
    >
      <div className="h-9 w-9">
        {isTabSelected && <IconCheckmark className="fill-blue-900" />}
      </div>
      {isTitle ? (
        <h3 className={titleClass}>{children}</h3>
      ) : (
        <button className={buttonClass} type="button" {...props}>
          {children}
        </button>
      )}
    </div>
  );
}

interface MobileNavbarProps {
  isVisible: boolean;
  handleSignOut: () => void;
}

export function MobileNavbar({ isVisible, handleSignOut }: MobileNavbarProps) {
  const shouldShowDiscoverTab = useShouldShowDiscoverTab();

  const firstNameQuery = useAccount({
    select: ({ firstName }) => firstName,
  });

  const selectedTab = useMapRouteToSelectedTab();

  const [isPaywallOpen, setIsPaywallOpen] = React.useState(false);
  const hasPaidPlan = proCheck.hasPaidPlan();

  const handleNavigation = (selectedItem: string) => {
    if (selectedItem === 'help') {
      window.open(`https://help.socrative.com`).focus();
      return;
    }
    Backbone.history.navigate(selectedItem, { trigger: true });
  };

  if (!isVisible) return null;
  return (
    <div
      data-testid="mobile-navbar"
      className="absolute inset-y-0 left-0 flex items-center tb:hidden"
    >
      <Menu>
        <MenuButton>
          {({ props }) => (
            <IconButton
              {...props}
              appearance="ghost"
              aria-label={translate('Navigation Menu')}
            >
              <FocusIndicator />
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                className="fill-grey-800"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M21 12a1 1 0 0 1-1 1H4a1 1 0 1 1 0-2h16a1 1 0 0 1 1 1Zm0 6a1 1 0 0 1-1 1H4a1 1 0 1 1 0-2h16a1 1 0 0 1 1 1Zm0-12a1 1 0 0 1-1 1H4a1 1 0 1 1 0-2h16a1 1 0 0 1 1 1Z" />
              </svg>
            </IconButton>
          )}
        </MenuButton>
        <MenuList className="absolute w-80 left-0 px-2 py-3 bg-white rounded-2 outline-none shadow-menu">
          <MenuItem onClick={() => handleNavigation(SelectableTab.Launch)}>
            {({ isActive, props }) => (
              <StyledMenuItem
                isActive={isActive}
                isTabSelected={selectedTab === SelectableTab.Launch}
                {...props}
              >
                {translate('Launch')}
              </StyledMenuItem>
            )}
          </MenuItem>
          <MenuItem onClick={() => handleNavigation(SelectableTab.Quizzes)}>
            {({ isActive, props }) => (
              <StyledMenuItem
                isActive={isActive}
                isTabSelected={selectedTab === SelectableTab.Quizzes}
                {...props}
              >
                {translate('Library')}
              </StyledMenuItem>
            )}
          </MenuItem>
          {shouldShowDiscoverTab && (
            <MenuItem onClick={() => handleNavigation(SelectableTab.Discover)}>
              {({ isActive, props }) => (
                <StyledMenuItem
                  isActive={isActive}
                  isTabSelected={selectedTab === SelectableTab.Discover}
                  {...props}
                >
                  {translate('Discover')}
                </StyledMenuItem>
              )}
            </MenuItem>
          )}
          <MenuItem onClick={() => handleNavigation(SelectableTab.Rooms)}>
            {({ isActive, props }) => (
              <StyledMenuItem
                isActive={isActive}
                isTabSelected={selectedTab === SelectableTab.Rooms}
                {...props}
              >
                {translate('Rooms')}
              </StyledMenuItem>
            )}
          </MenuItem>
          <MenuItem onClick={() => handleNavigation(Constants.REPORTS_TAB)}>
            {({ isActive, props }) => (
              <StyledMenuItem
                isActive={isActive}
                isTabSelected={selectedTab === SelectableTab.Reports}
                {...props}
              >
                {translate('Reports')}
              </StyledMenuItem>
            )}
          </MenuItem>
          <MenuItem onClick={() => handleNavigation(SelectableTab.LiveResults)}>
            {({ isActive, props }) => (
              <StyledMenuItem
                isActive={isActive}
                isTabSelected={selectedTab === SelectableTab.LiveResults}
                {...props}
              >
                {translate('Live Results')}
              </StyledMenuItem>
            )}
          </MenuItem>
          <div className="py-2">
            <MenuDivider />
          </div>
          <MenuItem>
            {({ props }) => (
              <StyledMenuItem
                isActive={false}
                isTitle={true}
                isTabSelected={false}
                {...props}
              >
                {firstNameQuery.data}
              </StyledMenuItem>
            )}
          </MenuItem>
          <MenuItem onClick={() => handleNavigation(SelectableTab.ProfileInfo)}>
            {({ isActive, props }) => (
              <StyledMenuItem
                isActive={isActive}
                isTabSelected={selectedTab === SelectableTab.ProfileInfo}
                {...props}
              >
                {translate('Profile')}
              </StyledMenuItem>
            )}
          </MenuItem>
          <MenuItem
            onClick={() => handleNavigation(SelectableTab.ProfileBilling)}
          >
            {({ isActive, props }) => (
              <StyledMenuItem
                isActive={isActive}
                isTabSelected={selectedTab === SelectableTab.ProfileBilling}
                {...props}
              >
                {translate('Plan and Members')}
              </StyledMenuItem>
            )}
          </MenuItem>
          <MenuItem onClick={() => handleNavigation('help')}>
            {({ isActive, props }) => (
              <StyledMenuItem isActive={isActive} {...props}>
                <span className="mr-8">{translate('Help Topics')}</span>
                <IconExternal
                  title={translate('External Link')}
                  titleId="help-topics-external-link"
                />
              </StyledMenuItem>
            )}
          </MenuItem>
          <MenuItem>
            {({ isActive, props }) => (
              <StyledMenuItem
                isActive={isActive}
                isTabSelected={false}
                {...props}
              >
                <span id="contact-intercom-support">
                  {translate('Contact') + ' Socrative'}
                </span>
              </StyledMenuItem>
            )}
          </MenuItem>
          <MenuItem onClick={handleSignOut}>
            {({ isActive, props }) => (
              <StyledMenuItem isActive={isActive} {...props}>
                {translate('Sign Out')}
              </StyledMenuItem>
            )}
          </MenuItem>
          {hasPaidPlan ? null : (
            <>
              <div className="py-2">
                <MenuDivider />
              </div>
              <MenuItem onClick={() => handleNavigation(Constants.PRICING)}>
                {({ isActive, props }) => (
                  <StyledMenuItem
                    isActive={isActive}
                    isTabSelected={false}
                    {...props}
                  >
                    {translate('Get')}
                    <Badge className="ml-1">Pro</Badge>
                  </StyledMenuItem>
                )}
              </MenuItem>
            </>
          )}
        </MenuList>
      </Menu>
      <PaywallDialogGeneric
        open={isPaywallOpen}
        onClose={() => setIsPaywallOpen(!isPaywallOpen)}
        paywallScreen={PaywallScreens.AppHeader}
      />
    </div>
  );
}
